import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Loader2, Plus, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useGenerateCallSummary } from '@/api/callSummary'
import { useCreateTask } from '@/api/tasks'

interface Props {
  callId: number
  resume: string | null
  /** The server is making the summary on its own: no button, it arrives live. */
  enCours?: boolean
  darkMode?: boolean
}

/**
 * Sous un appel du fil : copier le résumé (une icône), voir les tâches
 * proposées (calculées une fois avec le résumé et gardées par le serveur,
 * sans relancer l'IA), ou générer le résumé s'il manque. Les tâches
 * acceptées vont dans l'onglet Tâches de la fiche.
 */
export function ActionsResumeAppel({ callId, resume, enCours = false, darkMode = false }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const generer = useGenerateCallSummary()
  const creerTache = useCreateTask()
  const [copie, setCopie] = useState(false)
  const [suggestions, setSuggestions] = useState<string[] | null>(null)

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['contact-timeline'] })
    queryClient.invalidateQueries({ queryKey: ['contact-tasks'] })
  }

  const copier = async () => {
    if (!resume) return
    try {
      await navigator.clipboard.writeText(resume)
      setCopie(true)
      setTimeout(() => setCopie(false), 2000)
    } catch {
      /* presse-papiers refusé : rien à faire */
    }
  }

  const suggerer = async () => {
    try {
      const data = await generer.mutateAsync(callId)
      setSuggestions(data.suggested_tasks ?? [])
      if (!resume && data.ai_summary) rafraichir()
    } catch {
      setSuggestions([])
    }
  }

  const accepter = (titre: string, idx: number) => {
    creerTache.mutate(
      { title: titre, call_log: callId },
      {
        onSuccess: () => {
          setSuggestions((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev))
          queryClient.invalidateQueries({ queryKey: ['contact-tasks'] })
        },
      },
    )
  }

  const lien = cn(
    'inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium transition-colors',
    darkMode ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
  )

  if (!resume && enCours) {
    return (
      <div
        role="status"
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium',
          darkMode ? 'text-slate-400' : 'text-gray-500',
        )}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('callDetail.summaryInProgress', 'Résumé en cours…')}
      </div>
    )
  }

  if (!resume) {
    return (
      <div>
        <button type="button" onClick={suggerer} disabled={generer.isPending} className={lien}>
          {generer.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          {t('callDetail.generateSummary', 'Générer le résumé')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={copier}
          className={lien}
          aria-label={t('callDetail.copySummary', 'Copier le résumé')}
          title={copie ? t('callDetail.copied', 'Copié') : t('callDetail.copySummary', 'Copier le résumé')}
        >
          {copie ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={suggerer} disabled={generer.isPending} className={lien}>
          {generer.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          {t('callDetail.suggestTasks', 'Tâches proposées')}
        </button>
      </div>
      {suggestions && (
        suggestions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((titre, idx) => (
              <button
                key={titre}
                type="button"
                onClick={() => accepter(titre, idx)}
                disabled={creerTache.isPending}
                className={cn(
                  'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  darkMode
                    ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20'
                    : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
                )}
              >
                <Plus className="h-3 w-3" />
                {titre}
              </button>
            ))}
          </div>
        ) : (
          <p className={cn('text-[11px] px-1.5', darkMode ? 'text-slate-500' : 'text-gray-400')}>
            {t('callDetail.noSuggestion', 'Aucune tâche pour cet appel')}
          </p>
        )
      )}
    </div>
  )
}
